/**
 * CAR-1 — NRCS Web Soil Survey (SSURGO) provider.
 *
 * Pack: docs/prompt-packs/Coverage_AutoFill_Realworld_Research_Prompt.md (§CAR-1).
 *
 * Fills the `geotech` Coverage Matrix requirement (`feasibility[0]` in
 * `src/config/documentChecklists.ts`) with the four structured fields
 * the Author Evidence dialog expects:
 *
 *   - `soilClassification` (USCS class — `'SP'|'SM'|'SC'|'ML'|'CL'|'CH'|'OL'|'OH'|'Pt'`)
 *   - `bearingCapacity_kPa` (inferred, conservative — see
 *     `src/lib/geotechnical/bearingCapacity.ts`)
 *   - `boreholeDepth_m` (NRCS publishes a soil profile depth but NOT a
 *     boring log — left null so the dialog still prompts for a real geo)
 *   - `groundwaterDepth_m` (NRCS publishes the SSURGO water-table
 *     annual-minimum depth in cm; we convert to metres at the boundary)
 *
 * Source upstream — NRCS Soil Data Access REST POST /Tabular/post.rest.
 * The browser keeps the authenticated GET /api/proxy contract; the server
 * and headless provider transport translate only the canonical point query
 * to the fixed REST POST endpoint. ASMX RunQuery is SOAP/XML, not JSON GET.
 *
 * HARD RULE compliance:
 *   - Routes through `/api/proxy` via `fetchWithTimeout` — never direct.
 *   - Host (`sdmdataaccess.sc.egov.usda.gov`) MUST be in
 *     `ALLOWED_PROXY_HOSTS` in `server.ts`. Added in the CAR-1 ops commit.
 *   - Anonymous + free — `requiresKey: false`. Mirrors `wriAqueductProvider`
 *     for the no-key government API pattern.
 *
 * Hallucination guard (HARD RULE 8):
 *   - Every returned value cites the NRCS map-unit key (`mukey`) it came
 *     from. When SSURGO has no coverage (offshore / non-CONUS), the
 *     provider throws "no soil data for this location" — the resolver
 *     short-circuits and the Coverage Matrix surfaces an honest missing
 *     row instead of a fabricated soil class.
 *
 * Program integration:
 *   - The provider is registered in the catalog, dispatcher, rerun/status,
 *     SED, and report paths as a dedicated geotechnical SCREENING section.
 *     It never substitutes for professional borehole, settlement,
 *     liquefaction, foundation-design, or stamped-report evidence.
 *
 * Still out of scope:
 *   - USGS NWIS Groundwater cross-reference for sites where SSURGO's
 *     `wtdepannmin` is null. Documented in the pack at §CAR-1 sources.
 *   - State geological survey borehole logs (Arkansas, Texas, etc.).
 *     Per-state ArcGIS endpoints differ and need their own provider
 *     adapter — pack §CAR-1 "State Geological Survey APIs".
 *
 * Telemetry buckets — emitted to `[provider:nrcs:soil]` so Cloud Run logs
 * bucket failure modes consistently with the EL `[provider:elevation]`
 * taxonomy:
 *
 *   - 'ok'           — at least one SSURGO map unit returned for the point
 *   - 'http-error'   — non-200 from upstream
 *   - 'empty-rows'   — HTTP 200 but `Table` is empty (no coverage)
 *   - 'parse-error'  — JSON / response parse failed
 *   - 'network-error'— fetch threw / aborted
 *   - 'no-mukey'     — rows present but `mukey` not parseable
 */
import type { DataProvider, ProviderResult } from './types';
import { CACHE_TTL, fetchWithTimeout, gridCellKey } from './types';
import { refdataContains, tryRefdata } from '../refdata/refdataClient';
import { buildSsurgoQuery, NRCS_SDA_REST_URL } from '../../lib/providers/nrcsSda';

// ── USCS taxonomy ─────────────────────────────────────────────────────

/**
 * USCS soil-classification enum the `geotech` Coverage Matrix
 * requirement's authoredSchema declares
 * (`src/config/documentChecklists.ts:99`). Re-exported here so the
 * pure-transform `geotechCoverageFill.ts` can narrow values to the
 * exact enum the AuthorEvidenceDialog renders in its `<select>`.
 */
export type UscsClass = 'SP' | 'SM' | 'SC' | 'ML' | 'CL' | 'CH' | 'OL' | 'OH' | 'Pt';

// ── Public data shape ─────────────────────────────────────────────────

/**
 * NRCS SSURGO-derived geotechnical evidence for a single point.
 *
 * Each field can be null — SSURGO coverage varies by county and many
 * fields (e.g. `brockdepmin` for sites on bedrock) are sparsely
 * populated. Null is honest; we never default-fill from imagination
 * (HARD RULE 8).
 */
export interface GeotechnicalData {
  /** NRCS Map Unit Key — opaque stable id for the soil polygon the
   *  point landed in. Carried so the Author Evidence dialog can cite
   *  the exact source map unit in its notes string. */
  mukey: string;
  /** Map-unit name from SSURGO (`muname`) — e.g. "Loring silt loam,
   *  8 to 12 percent slopes". Null when not published. */
  mapUnitName: string | null;
  /** USCS class derived from the dominant component's USDA texture
   *  per NRCS NSSH Table 423-A.5. Null when SSURGO returns a texture
   *  we can't classify (rare). */
  uscsClass: UscsClass | null;
  /** USDA texture class (raw `texcl` from `chtexturegrp` / `chtexture`)
   *  — e.g. "silt loam", "sandy clay loam". Cited in the notes string
   *  so the auditor sees the original mapping input. */
  usdaTexture: string | null;
  /** SSURGO drainage class — e.g. "well drained", "poorly drained".
   *  Useful for the notes string but not a structured authoredSchema
   *  field. */
  drainageClass: string | null;
  /** Depth to bedrock, annual minimum (cm). SSURGO `brockdepmin`.
   *  Null when no bedrock encountered in the soil profile (typically
   *  > 2m in alluvial valleys). */
  depthToBedrockCm: number | null;
  /** Depth to seasonal water table, annual minimum (cm). SSURGO
   *  `wtdepannmin`. Null when no water table is recorded within the
   *  surveyed profile depth. */
  depthToWaterTableCm: number | null;
}

// ── Telemetry ─────────────────────────────────────────────────────────

type NrcsOutcomeBucket =
  | 'ok'
  | 'http-error'
  | 'empty-rows'
  | 'parse-error'
  | 'network-error'
  | 'no-mukey';

function logNrcsTelemetry(
  phase: 'request' | 'response',
  payload: Record<string, unknown>,
): void {
  // eslint-disable-next-line no-console
  console.info(`[provider:nrcs:soil] ${phase}`, payload);
}

// ── USDA texture → USCS map ───────────────────────────────────────────

/**
 * NRCS NSSH Table 423-A.5 mapping. Each USDA texture class maps to one
 * USCS class — the textbook engineering classification a civil engineer
 * would expect from a soil report. Keys are normalised lower-case raw
 * `texcl` strings from SSURGO; unrecognised inputs return null.
 *
 * Reference: USDA NRCS, National Soil Survey Handbook, Part 618.43
 * (Engineering Classification of Soils — Table 423-A.5). Reproduced
 * directly into the curated DAM via `geotech-001` in the pack.
 *
 * Conservative bias: when a USDA texture straddles two USCS classes
 * (e.g. "loam" can be ML or CL depending on fines plasticity), we pick
 * the LOWER-bearing class so the inferred bearing-capacity range stays
 * conservative.
 */
const USDA_TEXTURE_TO_USCS: Record<string, UscsClass> = {
  // ── Sands ──────────────────────────────────────────────────────────
  'sand':              'SP',
  'fine sand':         'SP',
  'coarse sand':       'SP',
  'very fine sand':    'SP',
  'loamy sand':        'SP',
  'loamy fine sand':   'SP',
  'loamy coarse sand': 'SP',
  // ── Silty / clayey sands ──────────────────────────────────────────
  'sandy loam':        'SM',
  'fine sandy loam':   'SM',
  'coarse sandy loam': 'SM',
  'very fine sandy loam': 'SM',
  'sandy clay loam':   'SC',
  'sandy clay':        'SC',
  // ── Silts ─────────────────────────────────────────────────────────
  'silt':              'ML',
  'silt loam':         'ML',
  'silty clay loam':   'ML',
  // ── Clays ─────────────────────────────────────────────────────────
  'loam':              'CL',
  'clay loam':         'CL',
  'clay':              'CH',
  'silty clay':        'CH',
  // ── Organics ──────────────────────────────────────────────────────
  'mucky':             'OL',
  'muck':              'OL',
  'mucky peat':        'OH',
  'peat':              'Pt',
};

/**
 * Look up the USCS class for a USDA texture string. Trims whitespace
 * and lowercases; unrecognised input → null.
 *
 * Exported so `geotechCoverageFill` can run the same mapping against
 * authored / DAM data without re-importing the table.
 */
export function uscsFromUsdaTexture(texture: string | null | undefined): UscsClass | null {
  if (!texture || typeof texture !== 'string') return null;
  const key = texture.trim().toLowerCase();
  return USDA_TEXTURE_TO_USCS[key] ?? null;
}

// ── SSURGO SQL ────────────────────────────────────────────────────────

export { buildSsurgoQuery } from '../../lib/providers/nrcsSda';

// ── Provider ──────────────────────────────────────────────────────────

const NRCS_SDA_BASE = NRCS_SDA_REST_URL;
const NRCS_SOIL_CACHE_CONTRACT_VERSION = 'v2-refdata-snapshot';

/**
 * NRCS SDA REST JSON responses wrap rows as a `Table` array.
 * The exact shape from the upstream:
 *
 *   { "Table": [ ["mukey", "muname", "drainagecl", "brockdepmin",
 *                 "wtdepannmin", "texcl"], ... ] }
 *
 * With `Format=JSON+COLUMNNAME` the first row is the column-name
 * header. We narrow that here so the parser is explicit.
 */
interface NrcsSdaResponse {
  Table?: Array<Array<string | number | null>>;
}

export const nrcsSoilSurveyProvider: DataProvider<GeotechnicalData> = {
  id: 'nrcs-soil-survey',
  name: 'NRCS Web Soil Survey (SSURGO)',
  cacheTtlMs: CACHE_TTL.CLIMATE_NORMALS, // soil maps update annually at most
  requiresKey: false, // anonymous, no API key required
  cacheKey: ({ lat, lng }) => `${NRCS_SOIL_CACHE_CONTRACT_VERSION}_${gridCellKey(lat, lng)}`,

  async fetch(lat: number, lng: number): Promise<ProviderResult<GeotechnicalData>> {
    // Internal GET-shaped request: the constrained transport adapter makes
    // the upstream REST POST in both browser-proxy and headless runtimes.
    const sql = buildSsurgoQuery(lat, lng);
    const params = new URLSearchParams({
      Query: sql,
      Format: 'JSON+COLUMNNAME',
    });
    const url = `${NRCS_SDA_BASE}?${params.toString()}`;

    logNrcsTelemetry('request', {
      caller: 'nrcsSoilSurveyProvider.fetch',
      lat,
      lng,
      upstream: NRCS_SDA_BASE,
    });

    // A promoted, complete national snapshot is authoritative. Staged raw
    // archives have no pointer and therefore cannot answer this lookup.
    const snapshot = await tryRefdata('nrcs-ssurgo', () => refdataContains('nrcs-ssurgo', { lat, lng }));
    let json: NrcsSdaResponse = {};
    if (snapshot) {
      // At a map-unit boundary, keep the same dominant-component preference
      // as the SDA SQL. Stable ids resolve equal component percentages.
      const features = [...snapshot.features].sort((a, b) =>
        (nullableNumber(b.props.comppct_r) ?? -1) - (nullableNumber(a.props.comppct_r) ?? -1)
        || a.id.localeCompare(b.id));
      const props = features[0]?.props;
      const notCompleted = props && (String(props.musym).toUpperCase() === 'NOTCOM'
        || /not completed/i.test(String(props.muname)));
      json = { Table: props && !notCompleted ? [
        ['mukey', 'muname', 'drainagecl', 'brockdepmin', 'wtdepannmin', 'texcl'],
        [props.mukey, props.muname, props.drainagecl, props.brockdepmin, props.wtdepannmin, props.texcl],
      ] : [] };
    } else {
      let res: Response;
      try {
        res = await fetchWithTimeout(url, 20000);
      } catch (e) {
        logNrcsTelemetry('response', {
          caller: 'nrcsSoilSurveyProvider.fetch',
          bucket: 'network-error' as NrcsOutcomeBucket,
          error: (e as Error)?.message ?? 'unknown',
        });
        throw e;
      }

      if (!res.ok) {
        logNrcsTelemetry('response', {
          caller: 'nrcsSoilSurveyProvider.fetch',
          bucket: 'http-error' as NrcsOutcomeBucket,
          httpStatus: res.status,
        });
        throw new Error(`NRCS SSURGO: ${res.status}`);
      }

      try {
        json = (await res.json()) as NrcsSdaResponse;
      } catch (e) {
        logNrcsTelemetry('response', {
          caller: 'nrcsSoilSurveyProvider.fetch',
          bucket: 'parse-error' as NrcsOutcomeBucket,
          error: (e as Error)?.message ?? 'unknown',
        });
        throw e;
      }

    }

    const table = json.Table;
    if (!Array.isArray(table) || table.length < 2) {
      // First row is the column-name header — we need at least one
      // data row, so length < 2 means "no SSURGO coverage at this
      // point". Offshore / Hawaii non-AG land / Alaska tundra commonly
      // hit this; permanent-unavailable bucket.
      logNrcsTelemetry('response', {
        caller: 'nrcsSoilSurveyProvider.fetch',
        bucket: 'empty-rows' as NrcsOutcomeBucket,
        rowCount: Array.isArray(table) ? table.length : 0,
      });
      // Message phrasing aligned with the orchestrator's
      // `classifyProviderFailure` permanent-unavailable matcher: the
      // substring `"no data for this location"` triggers the
      // permanent-unavailable bucket in `index.ts:138`.
      throw new Error('NRCS SSURGO: no data for this location (no SSURGO soil coverage)');
    }

    // Skip header row. We requested a fixed column order so positional
    // access matches the SELECT clause in `buildSsurgoQuery`.
    const row = table[1];
    if (!Array.isArray(row) || row.length < 6) {
      logNrcsTelemetry('response', {
        caller: 'nrcsSoilSurveyProvider.fetch',
        bucket: 'no-mukey' as NrcsOutcomeBucket,
        rowShape: Array.isArray(row) ? row.length : null,
      });
      throw new Error('NRCS SSURGO returned malformed row data');
    }

    const mukeyRaw = row[0];
    const mukey = mukeyRaw == null ? '' : String(mukeyRaw);
    if (!mukey) {
      logNrcsTelemetry('response', {
        caller: 'nrcsSoilSurveyProvider.fetch',
        bucket: 'no-mukey' as NrcsOutcomeBucket,
      });
      throw new Error('NRCS SSURGO returned a row without a mukey');
    }

    const mapUnitName = nullableString(row[1]);
    const drainageClass = nullableString(row[2]);
    const depthToBedrockCm = nullableNumber(row[3]);
    const depthToWaterTableCm = nullableNumber(row[4]);
    const usdaTexture = nullableString(row[5]);
    const uscsClass = uscsFromUsdaTexture(usdaTexture);

    const data: GeotechnicalData = {
      mukey,
      mapUnitName,
      uscsClass,
      usdaTexture,
      drainageClass,
      depthToBedrockCm,
      depthToWaterTableCm,
    };

    logNrcsTelemetry('response', {
      caller: 'nrcsSoilSurveyProvider.fetch',
      bucket: 'ok' as NrcsOutcomeBucket,
      mukey,
      uscsClass: uscsClass ?? null,
      hasWaterTable: depthToWaterTableCm != null,
    });

    return {
      data,
      sources: [{
        field: 'geotechnical',
        source:
          `NRCS Web Soil Survey (SSURGO) — Map unit ${mukey}` +
          (mapUnitName ? `: "${mapUnitName}"` : '') +
          (uscsClass ? ` · USCS ${uscsClass} (from "${usdaTexture}")` : '') +
          (depthToWaterTableCm != null ? ` · GW ${(depthToWaterTableCm / 100).toFixed(1)}m` : '')
          + (snapshot ? ` [GCloud snapshot ${snapshot.version}, vintage ${snapshot.vintageDate}]` : ''),
        url: 'https://websoilsurvey.sc.egov.usda.gov/App/HomePage.htm',
        retrievedAt: new Date().toISOString(),
        ...(snapshot ? { dataAsOf: snapshot.vintageDate } : {}),
      }],
    };
  },
};

// ── Internal narrowing helpers ────────────────────────────────────────

function nullableString(value: unknown): string | null {
  if (value == null) return null;
  if (typeof value !== 'string') return String(value).trim() || null;
  const t = value.trim();
  return t.length === 0 ? null : t;
}

function nullableNumber(value: unknown): number | null {
  if (value == null) return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'string') {
    const t = value.trim();
    if (t.length === 0) return null;
    const n = Number(t);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}
